import React, { useState, useEffect } from 'react';
import OrderHeader from './OrderHeader';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Grid, Container, Card } from '@mui/material';
import Footer from 'src/components/Footer';
import { OrderContent as OrdersContent } from './OrdersContent';
import { useAuth } from 'src/hooks/useAuth';
import { API } from '../../../config/config';
import { Orders } from './orders.interface';



const OrdersIndex = () => {
  const { enhancedFetch } = useAuth();
  const [orders, setOrders] = useState<Orders[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Orders[]>([]);
  const [sortedOrders, setSortedOrders] = useState<Orders[]>([]); // Add this state to store the sorted orders
  const [currentOrder, setCurrentOrder] = useState<Orders | null>(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [selectedSortStatus, setSelectedSortStatus] = useState<string>('');
  const [selectedFilterStatus, setSelectedFilterStatus] = useState<string>('');
  const [page, setPage] = useState(0); // Typically, page numbering starts at 0
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  // create the state for the sorting and filtering here and pass it as a prop to OrdersHeader so it will reflect:
  // Fetch Orders

  //function to fetch details:
  // Fetch Order Details when currentOrder is set
  useEffect(() => {
    if (currentOrder && typeof currentOrder === 'string') {
      setLoadingOrderDetails(true);
      enhancedFetch(`${API.HOST}/api/v1/orders/${currentOrder}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then((data) => {
          setCurrentOrder(data); // Update currentOrder with fetched details
        })
        .catch((error) => {
          console.error('Error fetching order details:', error);
        })
        .finally(() => {
          setLoadingOrderDetails(false);
        });
    }
  }, [currentOrder]);

  // Fetch Orders by Page
  useEffect(() => {
    setLoading(true);
    enhancedFetch(
      `${API.HOST}/api/v1/checkout-sessions?page=${page}&size=${rowsPerPage}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
      .then((data) => {
        setOrders(data.content);
        setTotalOrders(data.totalElements);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching orders:', error);
        // Handle error appropriately, e.g., show a notification
      });
  }, [page, rowsPerPage]);

  useEffect(() => {
    enhancedFetch(
      `${API.HOST}/api/v1/checkout-sessions?page=${page}&size=${rowsPerPage}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
      .then((data) => {
        setOrders(data.content);
        setTotalOrders(data.totalElements);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching orders:', error);
        // Handle error appropriately, e.g., show a notification
      });
  }, [refresh]);

  // Fetch Status Options
  useEffect(() => {
    enhancedFetch(`${API.HOST}/api/v1/order-statuses`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((data) => {
        setStatusOptions(data); // Assumes the API response is the array of statuses
      })
      .catch((error) => {
        console.error('Error fetching status options:', error);
      });
  }, []);

  // update based on filter state change
  useEffect(() => {
    if (selectedFilterStatus === '' || selectedFilterStatus === 'all') {
      // If no filter is selected, show all orders
      setFilteredOrders(orders);
    }
  }, [selectedFilterStatus, orders, refresh]);



  const updateOrderStatus = async (
    orderId: string,
    newStatus: string
  ): Promise<void> => {
    try {
      await enhancedFetch(
        `${API.HOST}/api/v1/orders/status/${orderId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        }
      );

      // Update the orders state locally
      setOrders((prevOrders) =>
        prevOrders?.map((order) =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );

      // Update filteredOrders if necessary
      setFilteredOrders((prevFiltered) =>
        prevFiltered?.map((order) =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
      // alert('Order status updated successfully!');
    } catch (error) {
      console.error('Error updating order status:', error);
      // alert('An error occurred while updating the order status.');
    }
  };

  return (
    <>
      <title>Orders</title>
      <PageTitleWrapper>
        <OrderHeader
          statusOptions={statusOptions}
          selectedSortStatus={selectedSortStatus}
          setStatusOptions={setStatusOptions}
          setSelectedSortStatus={setSelectedSortStatus}
          selectedFilterStatus={selectedFilterStatus}
          setSelectedFilterStatus={setSelectedFilterStatus}
        />
      </PageTitleWrapper>
      <Container maxWidth="lg">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12}>
            <Card>
              <OrdersContent
                onRefresh={() => {
                  setRefresh(!refresh);
                }}
                loading={loading}
                statusOptions={statusOptions}
                orders={selectedSortStatus ? sortedOrders : filteredOrders}
                setOrders={setOrders}
                currentOrder={currentOrder}
                setCurrentOrder={setCurrentOrder}
                updateOrderStatus={updateOrderStatus}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalOrders={totalOrders}
                setTotalOrders={setTotalOrders}
              />
            </Card>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </>
  );
};

export default OrdersIndex;
